import {Mapper} from '@s-ui/domain'

import {BooleanSharedValueObject} from '../../shared/ValueObjects/BooleanSharedValueObject.js'
import {AdListValueObjectLegacy} from '../ValueObjects/AdListValueObjectLegacy.js'

/**
 * @implements Mapper
 * Use the raw response from the http repository to create an Ad Details Value Object.
 */
export default class HttpAdDetailsResponseToAdDetailsAggregateMapper extends Mapper {
  constructor({
    config,
    adDetailsAggregateFactory,
    fromAdDetailsRawDataToAdEntityMapper,
    fromAdDetailsStatsRawDataToAdStatsValueObjectMapper,
    fromAdDetailsRawDataToAdTypeValueObjectMapper,
    fromAdDetailsRawDataToSellerValueObjectMapper,
    fromAdDetailsRawDataToVehicleCertificationValueObjectMapper,
    fromAdDetailsRawDataToVehicleHistoryValueObjectMapper,
    fromDealerRawDataToDealerEntityMapper,
    fromVehicleCertificationRawDataToVehicleCertificationVOMapper,
    fromAdDetailsRawDataToVehicleInfoValueObjectMapper,
    financingTermsRawDataToFinancingConditionEntityMapper,
    reservationEntityFactory
  }) {
    super()

    this._config = config
    const {AD_STATUS} = this._config.get('search')
    this._AD_STATUS = AD_STATUS
    this._appName = this._config.get('APP_NAME')
    this._culture = this._config.get('DEFAULT_CULTURE')
    this._adDetailsAggregateFactory = adDetailsAggregateFactory
    this._fromAdDetailsRawDataToAdEntityMapper = fromAdDetailsRawDataToAdEntityMapper
    this._fromAdDetailsStatsRawDataToAdStatsValueObjectMapper = fromAdDetailsStatsRawDataToAdStatsValueObjectMapper
    this._fromAdDetailsRawDataToAdTypeValueObjectMapper = fromAdDetailsRawDataToAdTypeValueObjectMapper
    this._fromAdDetailsRawDataToSellerValueObjectMapper = fromAdDetailsRawDataToSellerValueObjectMapper
    this._fromAdDetailsRawDataToVehicleCertificationValueObjectMapper =
      fromAdDetailsRawDataToVehicleCertificationValueObjectMapper
    this._fromAdDetailsRawDataToVehicleHistoryValueObjectMapper = fromAdDetailsRawDataToVehicleHistoryValueObjectMapper
    this._fromDealerRawDataToDealerEntityMapper = fromDealerRawDataToDealerEntityMapper
    this._fromVehicleCertificationRawDataToVehicleCertificationVOMapper =
      fromVehicleCertificationRawDataToVehicleCertificationVOMapper
    this._fromAdDetailsRawDataToVehicleInfoValueObjectMapper = fromAdDetailsRawDataToVehicleInfoValueObjectMapper
    this._financingTermsRawDataToFinancingConditionEntityMapper = financingTermsRawDataToFinancingConditionEntityMapper
    this._reservationEntityFactory = reservationEntityFactory
  }

  hasFinancingData = financingData =>
    financingData !== null && financingData !== undefined && Object.keys(financingData).length > 0

  map = async rawData => {
    const {ad} = rawData
    return this._adDetailsAggregateFactory({
      ad: await this._fromAdDetailsRawDataToAdEntityMapper.map(rawData),
      adStats: await this._fromAdDetailsStatsRawDataToAdStatsValueObjectMapper.map({
        stats: rawData.stats
      }),
      adType: await this._fromAdDetailsRawDataToAdTypeValueObjectMapper.map(rawData),
      ...(rawData.recommendedAds && {
        recommendedAds: AdListValueObjectLegacy.createFromRecommendedAds({value: rawData.recommendedAds}),
        isRecommendedAdsFallback: BooleanSharedValueObject.create({value: rawData.recommendedAdsFallback})
      }),
      financingData: this.hasFinancingData(rawData.financingData)
        ? this._financingTermsRawDataToFinancingConditionEntityMapper.map({...rawData.financingData})
        : undefined,
      ...(rawData.trade && {isTradable: BooleanSharedValueObject.create({value: rawData?.trade?.isTradable})}),
      reservation: this._reservationEntityFactory({...rawData.reservation}),
      seller: await this._fromAdDetailsRawDataToSellerValueObjectMapper.map(rawData),
      status:
        ad.status === this._AD_STATUS.ACTIVE || ad.status === this._AD_STATUS.PREVIEW
          ? this._AD_STATUS.ACTIVE
          : this._AD_STATUS.INACTIVE,
      vehicleCertification: await this._fromAdDetailsRawDataToVehicleCertificationValueObjectMapper.map(rawData),
      ...(rawData.vehicleHistory && {
        vehicleHistory: await this._fromAdDetailsRawDataToVehicleHistoryValueObjectMapper.map(rawData)
      }),
      vehicleInfo: await this._fromAdDetailsRawDataToVehicleInfoValueObjectMapper.map(rawData)
    })
  }
}
